import React, { createContext, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";

type Language = "he" | "ru";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
}) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Always initialize with Hebrew
  const [language, setLanguageState] = useState<Language>("he");

  // Track if this is the initial load
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Update HTML attributes and i18n when language changes
  const setLanguage = (lang: Language) => {
    setLanguageState(lang);

    // Note: Not saving to localStorage - always start with Hebrew

    // Update i18n
    i18n.changeLanguage(lang);

    // Update HTML attributes
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "he" ? "rtl" : "ltr";

    // Debug log to verify direction changes
    console.log("DIR=", document.documentElement.dir, "LANG=", lang);

    // Navigate to home page when language changes (but not on initial load)
    if (!isInitialLoad && location.pathname !== "/") {
      navigate("/");
    }
  };

  // Initialize HTML attributes on mount
  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = language === "he" ? "rtl" : "ltr";
    i18n.changeLanguage(language);

    // Mark that initial load is complete
    setIsInitialLoad(false);
  }, [language, i18n]);

  const value: LanguageContextType = {
    language,
    setLanguage,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
